/**
 * Contrato de dados entre YDChat e ERP YOURDRESS.
 *
 * O ERP é a fonte oficial de clientes, vendedores, produtos, variações,
 * estoque, preços, pedidos e vendas. O chat só guarda referências (ids do ERP)
 * e cópias de leitura com data de sincronização. Os endpoints que entregam
 * esses formatos estão descritos em docs/erp-api.md.
 */
import { z } from "zod";

export interface ClienteErp {
  id: string;
  empresaId: string;
  nome: string;
  telefone: string | null;
  email: string | null;
  /** Só vem preenchido quando o usuário do chat tem permissão de ver CPF. */
  cpfCnpj: string | null;
  dataNascimento: string | null;
  endereco: EnderecoErp | null;
  vendedoraId: string | null;
  vip: boolean;
  bloqueado: boolean;
  criadoEm: string;
}

export interface EnderecoErp {
  cep: string | null;
  logradouro: string | null;
  numero: string | null;
  complemento: string | null;
  bairro: string | null;
  cidade: string | null;
  uf: string | null;
}

export interface ResumoComercialErp {
  clienteId: string;
  quantidadeCompras: number;
  valorTotal: number;
  ticketMedio: number;
  ultimaCompraEm: string | null;
  pedidosRecentes: PedidoResumoErp[];
  pedidosPendentes: PedidoResumoErp[];
  etiquetas: string[];
  /** O que a cliente mais compra (pelas compras recentes). */
  preferencias: { tamanhos: string[]; cores: string[]; categorias: string[] };
}

export interface EmpresaErp {
  id: string;
  nome: string;
}

export const STATUS_PEDIDO_CHAT = [
  "iniciado",
  "aguardando_pagamento",
  "pago",
  "em_separacao",
  "enviado",
  "entregue",
  "cancelado",
] as const;
export type StatusPedidoChat = (typeof STATUS_PEDIDO_CHAT)[number];

export interface PedidoResumoErp {
  id: string;
  numero: number;
  canal: string;
  total: number;
  status: StatusPedidoChat;
  statusPagamento: string;
  criadoEm: string;
}

export interface VendedorErp {
  id: string;
  nome: string;
  ativo: boolean;
}

export interface ProdutoErp {
  id: string;
  nome: string;
  categoria: string | null;
  marca: string | null;
  tecido: string | null;
  descricao: string | null;
  fotoUrl: string | null;
  variacoes: VariacaoErp[];
  /** Página do produto na loja online (null: não publicado ou loja não ligada). */
  urlLoja?: string | null;
}

export interface VariacaoErp {
  id: string;
  produtoId: string;
  sku: string | null;
  codigoBarras: string | null;
  cor: string | null;
  tamanho: string | null;
  estampa: string | null;
  preco: number;
  /** estoque_atual - estoque_reservado, já calculado pelo ERP. */
  estoqueDisponivel: number;
  fotoUrl: string | null;
}

export interface FiltroProdutosErp {
  texto?: string;
  sku?: string;
  categoria?: string;
  cor?: string;
  tamanho?: string;
  somenteComEstoque?: boolean;
  limite?: number;
}

export interface NovoClienteErp {
  nome: string;
  telefone: string;
  email?: string | null;
  cpfCnpj?: string | null;
  endereco?: Partial<EnderecoErp> | null;
  vendedoraId?: string | null;
}

/** Resposta do cadastro: `existente` = o ERP já tinha a cliente (mesmo telefone ou CPF). */
export type ClienteCriadoErp = ClienteErp & { existente: boolean };

export const cadastroClienteSchema = z.object({
  nome: z.string().trim().min(2, "Informe o nome.").max(120),
  email: z.string().trim().email("E-mail inválido.").max(255).nullish().or(z.literal("")),
  cpfCnpj: z
    .string()
    .trim()
    .nullish()
    .refine((v) => !v || [11, 14].includes(v.replace(/\D/g, "").length), "CPF/CNPJ inválido."),
  dataNascimento: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .nullish()
    .or(z.literal("")),
  endereco: z
    .object({
      cep: z.string().trim().max(9).nullish(),
      logradouro: z.string().trim().max(120).nullish(),
      numero: z.string().trim().max(20).nullish(),
      complemento: z.string().trim().max(120).nullish(),
      bairro: z.string().trim().max(120).nullish(),
      cidade: z.string().trim().max(120).nullish(),
      uf: z.string().trim().max(2).nullish(),
    })
    .nullish(),
});
export type CadastroCliente = z.infer<typeof cadastroClienteSchema>;

export interface NovoPedidoErp {
  /** Idempotência: o mesmo id repetido nunca cria dois pedidos no ERP. */
  idExterno: string;
  clienteId: string;
  vendedoraId: string | null;
  /** Preço vem do ERP; o chat manda só variação, quantidade e desconto. */
  itens: { variacaoId: string; quantidade: number; desconto: number }[];
  desconto: number;
  frete: number;
  retirada: boolean;
  transportadorId: string | null;
  /** Nulo = endereço do cadastro da cliente. */
  endereco: Partial<EnderecoErp> | null;
  formaPagamento: string | null;
  observacoes: string | null;
}

export interface OpcoesVendaErp {
  formasPagamento: string[];
  transportadores: {
    id: string;
    nome: string;
    meio: "transportadora" | "delivery";
    ehCorreios: boolean;
  }[];
}

/** Eventos que o ERP manda para o chat (webhook assinado). */
export const EVENTOS_ERP = [
  "cliente.atualizado",
  "pedido.atualizado",
  "pagamento.confirmado",
  "estoque.atualizado",
] as const;
export type EventoErp = (typeof EVENTOS_ERP)[number];
